import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * PriceInquiryModal - Submit a price inquiry for a diamond
 *
 * Used when a diamond has inquiry-based pricing (no listed price).
 * Auto-fills user info from auth store; user only needs to add an optional message.
 */
import { useState } from 'react';
import { FaTag } from 'react-icons/fa';
import { apiClient } from '../utils/apiClient';
import { notify } from '../utils/notifications';
import { activityTracker } from '../services/activityTracker';
import { useAuthStore } from '../stores/authStore';
import { BaseModal, Section, SectionTitle, Textarea, PrimaryButton, GhostButton, } from './shared/BaseModal';
import styled from 'styled-components';
const DiamondBanner = styled.div `
  background: var(--bg-tertiary);
  border: 1px solid var(--accent-gold, #996515);
  border-left: 4px solid var(--accent-gold, #996515);
  padding: 16px 20px;
  margin-bottom: 20px;
`;
const BannerTitle = styled.div `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 16px;
  color: var(--accent-gold, #996515);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;
const BannerSpecs = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 13px;
  color: var(--text-secondary);
`;
const BannerSpec = styled.span `
  &:not(:last-child)::after {
    content: '·';
    margin-left: 12px;
    color: var(--text-tertiary);
  }
`;
const UserInfoRow = styled.div `
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: var(--bg-tertiary);
  font-size: 14px;
  color: var(--text-secondary);

  strong {
    color: var(--text-primary);
    margin-right: 6px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
  }
`;
const SubmitInfo = styled.p `
  font-size: 12px;
  color: var(--text-tertiary);
  margin-top: 12px;
  line-height: 1.5;
`;
export const PriceInquiryModal = ({ isOpen, onClose, diamond }) => {
    const { user } = useAuthStore();
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const userName = user?.profile
        ? `${user.profile.firstName} ${user.profile.lastName}`.trim()
        : user?.email || '';
    const caratWeight = diamond.caratWeight || parseFloat(diamond.carat || '0');
    const lab = diamond.lab || diamond.certification || '';
    const handleSubmit = async () => {
        if (!user)
            return;
        try {
            setSubmitting(true);
            const response = await apiClient.post('price-inquiry/create', {
                name: userName,
                diamondId: diamond.id,
                stockNumber: diamond.stockNumber || null,
                shape: diamond.shape,
                caratWeight,
                color: diamond.color,
                clarity: diamond.clarity,
                lab,
                certificateNumber: diamond.certificateNumber || null,
                message: message.trim() || null,
            });
            if (response.ok && response.data?.success) {
                notify.success('Inquiry Submitted', 'We will respond to your price inquiry within 24 hours.');
                activityTracker.trackAction('price-inquiry-submitted', `Price inquiry for ${diamond.shape} ${caratWeight}ct ${diamond.color} ${diamond.clarity}`, {
                    diamondId: diamond.id,
                    stockNumber: diamond.stockNumber,
                    inquiryId: response.data.inquiryId,
                });
                setMessage('');
                onClose();
            }
            else {
                notify.error('Submission Failed', response.error || 'Failed to submit price inquiry');
            }
        }
        catch (err) {
            notify.error('Submission Failed', 'Network error. Please try again.');
        }
        finally {
            setSubmitting(false);
        }
    };
    return (_jsxs(BaseModal, { isOpen: isOpen, onClose: onClose, title: "Inquire Price", titleAccent: "Price", size: "md", footer: _jsxs(_Fragment, { children: [_jsx(GhostButton, { onClick: onClose, disabled: submitting, children: "Cancel" }), _jsxs(PrimaryButton, { onClick: handleSubmit, disabled: submitting, children: [_jsx(FaTag, { size: 12 }), submitting ? 'Submitting...' : 'Submit Inquiry'] })] }), children: [_jsxs(DiamondBanner, { children: [_jsxs(BannerTitle, { children: [_jsx(FaTag, { size: 14 }), "Diamond Details"] }), _jsxs(BannerSpecs, { children: [_jsx(BannerSpec, { children: diamond.shape }), _jsxs(BannerSpec, { children: [caratWeight, "ct"] }), _jsx(BannerSpec, { children: diamond.color }), _jsx(BannerSpec, { children: diamond.clarity }), _jsx(BannerSpec, { children: lab }), diamond.stockNumber && _jsxs(BannerSpec, { children: ["Stock #", diamond.stockNumber] })] })] }), _jsxs(UserInfoRow, { children: [_jsxs("div", { children: [_jsx("strong", { children: "Name:" }), " ", userName] }), _jsxs("div", { children: [_jsx("strong", { children: "Email:" }), " ", user?.email] })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "Message (Optional)" }), _jsx(Textarea, { value: message, onChange: (e) => setMessage(e.target.value), placeholder: "Add any specific requirements, quantity interest, or questions about this diamond...", rows: 4, disabled: submitting })] }), _jsx(SubmitInfo, { children: "Your inquiry will be reviewed by our sales team. We typically respond within 24 hours during business days." })] }));
};
